import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiEdit3,
  FiTrash2,
  FiChevronDown,
  FiActivity
} from 'react-icons/fi';
import MedicalConditionService from '../../services/medicalconditionService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';

const MedicalConditions = ({ onCountUpdate, componentName = 'MedicalConditions' }) => {
  const location = useLocation();
  
  // **OBTENER DATOS DEL PACIENTE DESDE NAVIGATION STATE**
  const patientData = location.state;
  const patientId = patientData?.patientId;

  const [conditions, setConditions] = useState([]);
  const [filteredConditions, setFilteredConditions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  
  // Estado para Add New offcanvas
  const [showAddOffcanvas, setShowAddOffcanvas] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    currentStatus: '',
    reportedDate: '',
    endDate: '',
    status: 'All'
  });

  // **FUNCIÓN PRINCIPAL PARA CARGAR DATOS**
const loadConditions = useCallback(async () => {
  console.log('🔄 loadConditions called with:');
  console.log('- patientId:', patientId);
  console.log('- typeof patientId:', typeof patientId);
  console.log('- patientData:', patientData);
  
  if (!patientId) {
    console.log('❌ No patientId provided, component will show empty state');
    setConditions([]);
    onCountUpdate && onCountUpdate(componentName, 0);
    return;
  }
  
  // Validar formato del patientId
  if (typeof patientId !== 'string' || !/^[0-9a-fA-F]{24}$/.test(patientId)) {
    console.error('❌ Invalid patientId format:', patientId);
    console.error('❌ Expected: 24 character hexadecimal string');
    setConditions([]);
    onCountUpdate && onCountUpdate(componentName, 0);
    return;
  }
  
  setIsLoading(true);
  try {
    console.log('🔄 Loading conditions for valid patient:', patientId);
    const response = await MedicalConditionService.getConditionsByPatient(patientId);
    console.log('📋 Raw API response:', response);
    
    let conditionsData = [];
    
    if (response.data) {
      conditionsData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (Array.isArray(response)) {
      conditionsData = response;
    } else if (response.conditions) {
      conditionsData = Array.isArray(response.conditions) ? response.conditions : [response.conditions];
    }

    const normalizedConditions = conditionsData.map(condition => {
      console.log('Processing condition:', condition);
      
      // Handle patient information (could be populated or just ID)
      const patientInfo = typeof condition.patient === 'object' && condition.patient 
        ? { id: condition.patient._id, name: condition.patient.name }
        : { id: condition.patient || patientId, name: patientData.patientName || 'Unknown Patient' };

      // Handle addedBy information (could be populated or just ID)
      const addedByInfo = typeof condition.addedby === 'object' && condition.addedby
        ? { id: condition.addedby._id, name: condition.addedby.name, email: condition.addedby.email }
        : { id: condition.addedby, name: 'Unknown', email: '' };

      // Handle disease information (could be populated or just ID)
      const diseaseInfo = typeof condition.disease === 'object' && condition.disease
        ? { id: condition.disease._id, name: condition.disease.name, description: condition.disease.description }
        : { id: condition.disease, name: 'Unknown Disease', description: '' };

      return {
        // Primary identifiers
        condition_id: condition._id || condition.condition_id || condition.id,
        
        // Condition information
        condition_name: condition.conditionname || '',
        disease_name: diseaseInfo.name,
        disease_description: diseaseInfo.description,
        current_status: condition.currentstatus || '',
        
        // Dates
        reported_on: condition.reportedon || condition.createdAt || condition.reported_date,
        resolved_on: condition.resolvedon || condition.resolved_date || null,
        created_date: condition.createdAt || condition.created_date,
        updated_date: condition.updatedAt || condition.updated_date,
        
        // Personnel
        added_by: addedByInfo.name,
        added_by_id: addedByInfo.id,
        added_by_email: addedByInfo.email,
        
        // Additional data
        comments: condition.comments || '',
        status: condition.status || '1',
        
        // Patient info
        patient_id: patientInfo.id,
        patient_name: patientInfo.name,
        
        // Preserve original for detailed view
        _original: condition
      };
    });

    console.log('Normalized conditions:', normalizedConditions);
    console.log(`📊 ${componentName}: Actualizando contador a ${normalizedConditions.length}`);
    
    setConditions(normalizedConditions);
    onCountUpdate && onCountUpdate(componentName, normalizedConditions.length);
    
  } catch (error) {
    console.error('❌ Error loading conditions:', error);
    setConditions([]);
    onCountUpdate && onCountUpdate(componentName, 0);
  } finally {
    setIsLoading(false);
  }
}, [patientId, onCountUpdate, componentName, patientData]); // REMOVER conditions.length de las dependencias


  // **FUNCIÓN SEPARADA PARA CARGAR ESTADÍSTICAS**
  const loadConditionsStats = useCallback(async () => {
    if (!patientId || conditions.length === 0) return;
    
    try {
      const ongoingCount = conditions.filter(condition => 
        condition.current_status?.toLowerCase() === 'ongoing'
      ).length;
      
      const activeCount = conditions.filter(condition => 
        condition.status === '1'
      ).length;
      
      if (ongoingCount > 0 || activeCount > 0) {
        setAlertInfo({
          alert_type: 'condition',
          message: `${activeCount} active conditions${ongoingCount > 0 ? `, ${ongoingCount} ongoing` : ''}`,
          priority: ongoingCount > 0 ? 'high' : 'medium',
          requires_action: ongoingCount > 0
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading conditions stats:', error);
      setAlertInfo(null);
    }
  }, [patientId, conditions]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Conditions array length:', conditions.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...conditions];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(condition => 
        (condition.condition_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (condition.disease_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (condition.current_status || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (condition.comments || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Aplicar filtros específicos
    if (filters.status !== 'All') {
      if (filters.status === 'Active') {
        filtered = filtered.filter(condition => condition.status === '1');
      } else if (filters.status === 'Inactive') {
        filtered = filtered.filter(condition => condition.status === '0');
      }
    }

    if (filters.currentStatus) {
      filtered = filtered.filter(condition => 
        (condition.current_status || '').toLowerCase() === filters.currentStatus.toLowerCase()
      );
    }

    if (filters.reportedDate) {
      filtered = filtered.filter(condition => {
        const reportedDate = condition.reported_on;
        return reportedDate && new Date(reportedDate) >= new Date(filters.reportedDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(condition => {
        const reportedDate = condition.reported_on;
        return reportedDate && new Date(reportedDate) <= new Date(filters.endDate);
      });
    }

    console.log('Filtered array length:', filtered.length);

    // Ordenamiento
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.reported_on || item.created_date || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'status': {
          const statusOrder = { 'ongoing': 3, 'observation': 2, 'resolved': 1 };
          const statusA = (a.current_status || '').toLowerCase();
          const statusB = (b.current_status || '').toLowerCase();
          return (statusOrder[statusB] || 0) - (statusOrder[statusA] || 0);
        }
        case 'alphabetical':
          const conditionA = (a.condition_name || a.disease_name || '').toLowerCase();
          const conditionB = (b.condition_name || b.disease_name || '').toLowerCase();
          return conditionA.localeCompare(conditionB);
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredConditions(filtered);
  }, [conditions, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'status': return 'By Status';
      case 'alphabetical': return 'A-Z';
      default: return 'Sort by';
    }
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'condition_name',
      header: 'Condition',
      size: 200,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div>
            <div className="fw-bold text-primary">
              {row.original.condition_name || row.original.disease_name || 'Unknown Condition'}
            </div>
            <small className="text-muted">
              {formatDate(row.original.reported_on)}
            </small>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'current_status',
      header: 'Status',
      size: 120,
      Cell: ({ row }) => (
        <span className={`badge bg-${getStatusColor(row.original.current_status)} d-flex align-items-center gap-1`}>
          {getStatusIcon(row.original.current_status)}
          <span className="text-capitalize">
            {row.original.current_status || 'Unknown'}
          </span>
        </span>
      )
    },
    {
      accessorKey: 'disease_name',
      header: 'Disease',
      size: 150,
      Cell: ({ row }) => (
        <div>
          <div className="text-wrap">
            {row.original.disease_name || 'Not specified'}
          </div>
        </div>
      )
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      size: 200,
      Cell: ({ row }) => (
        <div>
          <div className="text-wrap text-truncate" style={{ maxWidth: '200px' }}>
            {row.original.comments || 'No comments'}
          </div>
        </div>
      )
    },
    {
      accessorKey: 'resolved_on',
      header: 'Resolution',
      size: 120,
      Cell: ({ row }) => (
        <div>
          {row.original.resolved_on ? (
            <div>
              <small className="text-success">Resolved</small>
              <br />
              <small className="text-muted">
                {formatDate(row.original.resolved_on)}
              </small>
            </div>
          ) : (
            <span className="badge bg-info">Ongoing</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Record Status',
      size: 100,
      Cell: ({ row }) => (
        <span className={`badge bg-${getRecordStatusColor(row.original.status)}`}>
          {getRecordStatusText(row.original.status)}
        </span>
      )
    },
    {
      accessorKey: 'added_by',
      header: 'Added By',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.added_by || 'Unknown'}</div>
          <small className="text-muted">
            {formatDate(row.original.created_date)}
          </small>
        </div>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewCondition(row.original.condition_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditCondition(row.original.condition_id);
            }}
            title="Edit"
          >
            <FiEdit3 />
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCondition(row.original.condition_id);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ], [conditions]);

useEffect(() => {
  console.log('🔍 useEffect principal - Cargando datos iniciales');
  console.log('- patientId:', patientId);
  console.log('- patientData:', patientData);
  
  // Cargar datos inmediatamente cuando el componente se monta o cambia el patientId
  loadConditions();
}, [loadConditions, patientId]); // Dependencias: loadConditions y patientId


  // **EFECTO PRINCIPAL PARA CARGAR DATOS**
useEffect(() => {
  console.log('🔍 DEBUGGING PATIENT DATA:');
  console.log('- location.state:', location.state);
  console.log('- patientData:', patientData);
  console.log('- patientId:', patientId);
  console.log('- typeof patientId:', typeof patientId);
  console.log('- patientId valid ObjectId?:', /^[0-9a-fA-F]{24}$/.test(patientId));
  
  if (!patientId) {
    console.error('❌ NO PATIENT ID FOUND!');
    console.error('❌ Check navigation state and URL parameters');
  } else {
    // Si tenemos patientId válido, cargar los datos
    console.log('✅ Patient ID found, loading conditions...');
    loadConditions();
  }
}, [patientId, patientData, location.state, loadConditions]);

  // **EFECTO SEPARADO PARA ESTADÍSTICAS**
  useEffect(() => {
    if (conditions.length > 0) {
      loadConditionsStats();
    }
  }, [conditions, loadConditionsStats]);

  // **EFECTO PARA ACTUALIZAR CONTADOR CUANDO CAMBIEN LOS DATOS**
  // useEffect(() => {
  //   console.log(`📊 ${componentName}: Actualizando contador a ${conditions.length}`);
  //   onCountUpdate && onCountUpdate(componentName, conditions.length);
  // }, [conditions.length, onCountUpdate, componentName]);

  // Apply filters whenever conditions or filters change
  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- conditions.length:', conditions.length);
    console.log('- sortBy:', sortBy);
    
    if (conditions.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay conditions, limpiando filteredConditions');
      setFilteredConditions([]);
    }
  }, [conditions, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showAddOffcanvas || showViewOffcanvas || showEditOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showAddOffcanvas, showViewOffcanvas, showEditOffcanvas]);

  // Solo mostrar loading si estamos esperando datos específicos
  if (isLoading && patientId) {
    return <div>Loading patient data...</div>;
  }

  const handleViewCondition = (conditionId) => {
    console.log('Viewing condition:', conditionId);
    const condition = conditions.find(c => c.condition_id === conditionId);
    if (condition) {
      console.log('Found condition for viewing:', condition);
      setSelectedCondition(condition);
      setShowViewOffcanvas(true);
    } else {
      console.error('Condition not found:', conditionId);
    }
  };
  
  const handleEditCondition = (conditionId) => {
    console.log('Editing condition:', conditionId);
    const condition = conditions.find(c => c.condition_id === conditionId);
    if (condition) {
      console.log('Found condition for editing:', condition);
      setEditingCondition(condition);
      setShowEditOffcanvas(true);
    } else {
      console.error('Condition not found for editing:', conditionId);
    }
  };

  const handleAddNew = () => {
    console.log('Add New button clicked - opening modal');
    setShowFilterOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowAddOffcanvas(true);
  };

// **FUNCIÓN COMPLETA PARA MANEJAR LA CREACIÓN DE CONDICIÓN MÉDICA**
const handleSubmitNewCondition = async (conditionData) => {
  console.log('=== SUBMITTING NEW CONDITION - START ===');
  console.log('1. Original condition data received:', conditionData);
  
  setIsSubmitting(true);
  
  try {
    // **PASO 1: OBTENER DATOS DEL USUARIO Y ECOSISTEMA ACTUAL**
    const currentUser = getCurrentUser();
    const currentEcosystem = getCurrentEcosystem();
    
    console.log('2. Current user obtained:', currentUser);
    console.log('3. Current ecosystem obtained:', currentEcosystem);
    console.log('4. Patient ID from component:', patientId);
    console.log('5. Patient data from component:', patientData);
    
    // **PASO 2: VALIDACIONES CRÍTICAS**
    if (!currentUser || !currentUser.id) {
      const error = 'No se pudo obtener información del usuario actual. Por favor, inicia sesión nuevamente.';
      console.error('❌ VALIDATION ERROR - User:', error);
      throw new Error(error);
    }
    
    if (!currentEcosystem || !currentEcosystem.id) {
      const error = 'No se pudo obtener información del ecosistema actual. Por favor, verifica tu configuración.';
      console.error('❌ VALIDATION ERROR - Ecosystem:', error);
      throw new Error(error);
    }

    if (!patientId) {
      const error = 'No se pudo obtener el ID del paciente.';
      console.error('❌ VALIDATION ERROR - Patient ID:', error);
      throw new Error(error);
    }

    // **PASO 3: PREPARAR DATOS PARA LA API - AQUÍ ESTÁ EL FIX PRINCIPAL**
    const completeConditionData = {
      // Datos básicos de la condición
      conditionname: conditionData.conditionname?.trim() || '',
      currentstatus: conditionData.currentstatus || 'ongoing',
      reportedon: conditionData.reportedon || new Date().toISOString().split('T')[0],
      resolvedon: conditionData.resolvedon && conditionData.resolvedon.trim() 
        ? conditionData.resolvedon 
        : null,
      comments: conditionData.comments?.trim() || '',
      
      // IDs requeridos por el backend - FIX: Asegurar que patientId sea válido
      patient: patientId, // CAMBIO: Usar directamente patientId del componente
      addedby: currentUser.id,
      ecosystemid: currentEcosystem.id,
      
      // Status por defecto (activo)
      status: '1'
    };

    // **VERIFICACIÓN ADICIONAL DEL PATIENT ID**
    console.log('6. Patient ID being sent:', completeConditionData.patient);
    console.log('6.1 Patient ID type:', typeof completeConditionData.patient);
    console.log('6.2 Patient ID length:', completeConditionData.patient?.length);
    
    // Validar que el patientId sea un ObjectId válido
    if (!completeConditionData.patient || typeof completeConditionData.patient !== 'string') {
      throw new Error('Patient ID is invalid or missing');
    }
    
    // Verificar formato de ObjectId (24 caracteres hexadecimales)
    if (!/^[0-9a-fA-F]{24}$/.test(completeConditionData.patient)) {
      console.warn('⚠️ Patient ID format warning:', completeConditionData.patient);
      console.warn('⚠️ Expected: 24 hexadecimal characters');
      // No lanzar error, pero registrar la advertencia
    }

    // **PASO 4: MANEJO ESPECIAL DEL CAMPO DISEASE**
    // Solo incluir disease si es un ObjectId válido de MongoDB
    if (conditionData.disease && conditionData.disease.trim()) {
      const diseaseValue = conditionData.disease.trim();
      
      // Verificar si parece un ObjectId válido (24 caracteres hexadecimales)
      if (/^[0-9a-fA-F]{24}$/.test(diseaseValue)) {
        completeConditionData.disease = diseaseValue;
        console.log('7. Disease ObjectId included:', diseaseValue);
      } else {
        // Si no es un ObjectId válido, no incluir el campo
        console.warn('⚠️ Disease value is not a valid ObjectId, excluding from request:', diseaseValue);
        console.warn('⚠️ Expected format: 24 hexadecimal characters (e.g., 507f1f77bcf86cd799439011)');
      }
    } else {
      console.log('7. No disease specified, excluding from request');
    }

    console.log('8. Final data prepared for API:', completeConditionData);
    
    // **PASO 5: LLAMAR AL SERVICIO**
    console.log('9. Calling MedicalConditionService.createCondition...');
    const result = await MedicalConditionService.createCondition(completeConditionData);
    
    console.log('10. Response received:', result);
    console.log('11. Condition created successfully with ID:', result?.data?._id || result?._id || 'ID not found');
    
    // **PASO 6: RECARGAR DATOS**
    console.log('12. Reloading conditions and stats...');
    await Promise.all([
      loadConditions(),
      loadConditionsStats()
    ]);
    
    console.log('13. Data reloaded successfully');
    
    // **PASO 7: CERRAR MODAL Y MOSTRAR CONFIRMACIÓN**
    setShowAddOffcanvas(false);
    
    console.log('14. Modal closed, showing success message');
    
    // Mostrar mensaje de éxito
    const successMessage = 'Medical condition created successfully!';
    if (window.showSuccessMessage) {
      window.showSuccessMessage(successMessage);
    } else if (window.Swal) {
      window.Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: successMessage,
        timer: 3000,
        showConfirmButton: false
      });
    } else {
      alert(successMessage);
    }
    
    console.log('=== CONDITION CREATION COMPLETED SUCCESSFULLY ===');
    
  } catch (error) {
    console.error('=== CONDITION CREATION FAILED ===');
    console.error('❌ Error type:', error.constructor.name);
    console.error('❌ Error message:', error.message);
    console.error('❌ Full error object:', error);
    
    // **MANEJO DETALLADO DE ERRORES**
    let errorMessage = 'Error creating medical condition. Please try again.';
    let statusCode = 'Unknown';
    
    if (error.response) {
      // Error de respuesta HTTP
      statusCode = error.response.status;
      console.error('❌ HTTP Status:', error.response.status);
      console.error('❌ Response headers:', error.response.headers);
      console.error('❌ Response data:', error.response.data);
      
      // Extraer mensaje de error específico
      if (error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.errors) {
          // Manejar errores de validación múltiples
          if (Array.isArray(error.response.data.errors)) {
            errorMessage = error.response.data.errors.join(', ');
          } else if (typeof error.response.data.errors === 'object') {
            errorMessage = Object.values(error.response.data.errors).join(', ');
          }
        }
      }
      
      // Errores HTTP específicos
      switch (error.response.status) {
        case 400:
          errorMessage = `Bad Request: ${errorMessage}`;
          break;
        case 401:
          errorMessage = 'Authentication required. Please login again.';
          break;
        case 403:
          errorMessage = 'Access denied. You don\'t have permission to create conditions.';
          break;
        case 404:
          errorMessage = 'API endpoint not found. Please check your configuration.';
          break;
        case 422:
          errorMessage = `Validation Error: ${errorMessage}`;
          break;
        case 500:
          errorMessage = 'Server error. Please try again later or contact support.';
          break;
      }
      
    } else if (error.request) {
      // Error de red/conexión
      console.error('❌ Network error - Request was made but no response received');
      console.error('❌ Request config:', error.config);
      errorMessage = 'Network error. Please check your internet connection and try again.';
      statusCode = 'Network Error';
      
    } else if (error.message) {
      // Error en la configuración de la petición o lógica
      errorMessage = error.message;
      console.error('❌ Configuration/Logic error:', error.message);
    }
    
    console.error('❌ Final error message to display:', errorMessage);
    console.error('❌ Status code:', statusCode);
    
    // **MOSTRAR ERROR AL USUARIO**
    const fullErrorMessage = `Error (${statusCode}): ${errorMessage}`;
    
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Creation Failed',
        text: fullErrorMessage,
        footer: 'Check the console for technical details',
        confirmButtonColor: '#dc3545'
      });
    } else {
      alert(fullErrorMessage);
    }
    
    // Re-lanzar el error para que otros manejadores puedan actuar si es necesario
    throw error;
    
  } finally {
    // **LIMPIAR ESTADO SIN IMPORTAR EL RESULTADO**
    console.log('15. Cleaning up - Setting isSubmitting to false');
    setIsSubmitting(false);
    console.log('=== CONDITION CREATION PROCESS END ===');
  }
};

  const handleUpdateCondition = async (updatedData) => {
    console.log('=== UPDATING CONDITION ===');
    console.log('Updated condition data:', updatedData);
    console.log('Editing condition ID:', editingCondition?.condition_id);
    
    setIsSubmitting(true);
    
    try {
      if (!editingCondition?.condition_id) {
        throw new Error('No se pudo obtener el ID de la condición a actualizar');
      }

      // Preparar datos para actualización
      const updateData = {
        conditionname: updatedData.conditionname || '',
        disease: updatedData.disease || null,
        currentstatus: updatedData.currentstatus || 'ongoing',
        reportedon: updatedData.reportedon,
        resolvedon: updatedData.resolvedon || null,
        comments: updatedData.comments || ''
      };

      console.log('Update data to send:', updateData);
      
      // Actualizar la condición usando el service
      const result = await MedicalConditionService.updateCondition(editingCondition.condition_id, updateData);
      console.log('Condition updated successfully:', result);
      
      // Recargar datos para obtener el nuevo estado
      await loadConditions();
      await loadConditionsStats();
      
      // Cerrar modal y mostrar éxito
      setShowEditOffcanvas(false);
      setEditingCondition(null);
      
      if (window.showSuccessMessage) {
        window.showSuccessMessage('Condition updated successfully!');
      } else {
        alert('Condition updated successfully!');
      }
      
    } catch (error) {
      console.error('Error updating condition:', error);
      
      let errorMessage = 'Error updating condition. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      alert(`Error: ${errorMessage}`);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // **FUNCIONES AUXILIARES PARA OBTENER DATOS DEL USUARIO**
  const getCurrentUser = () => {
    console.log('Getting current user...');
    
    try {
      const userData = localStorage.getItem('currentUser') || localStorage.getItem('user') || localStorage.getItem('authUser');
      if (userData) {
        const user = JSON.parse(userData);
        console.log('User found in localStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }

    try {
      const sessionUserData = sessionStorage.getItem('currentUser') || sessionStorage.getItem('user');
      if (sessionUserData) {
        const user = JSON.parse(sessionUserData);
        console.log('User found in sessionStorage:', user);
        return {
          id: user._id || user.id || user.userId,
          name: user.name || user.username || user.fullName,
          email: user.email
        };
      }
    } catch (error) {
      console.error('Error parsing user data from sessionStorage:', error);
    }

    console.warn('No user data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439011',
      name: 'Test User',
      email: 'test@example.com'
    };
  };

  const getCurrentEcosystem = () => {
    console.log('Getting current ecosystem...');
    
    try {
      const ecosystemData = localStorage.getItem('currentEcosystem') || localStorage.getItem('ecosystem');
      if (ecosystemData) {
        const ecosystem = JSON.parse(ecosystemData);
        console.log('Ecosystem found in localStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from localStorage:', error);
    }

    try {
      const sessionEcosystemData = sessionStorage.getItem('currentEcosystem') || sessionStorage.getItem('ecosystem');
      if (sessionEcosystemData) {
        const ecosystem = JSON.parse(sessionEcosystemData);
        console.log('Ecosystem found in sessionStorage:', ecosystem);
        return {
          id: ecosystem._id || ecosystem.id || ecosystem.ecosystemId,
          name: ecosystem.name || ecosystem.ecosystemName
        };
      }
    } catch (error) {
      console.error('Error parsing ecosystem data from sessionStorage:', error);
    }

    console.warn('No ecosystem data found, using default values for testing');
    return {
      id: '507f1f77bcf86cd799439012',
      name: 'Test Ecosystem'
    };
  };

  const handleDeleteCondition = async (conditionId) => {
    if (window.confirm('Are you sure you want to delete this condition?')) {
      try {
        await MedicalConditionService.deleteCondition(conditionId);
        await loadConditions();
        await loadConditionsStats();
      } catch (error) {
        console.error('Error deleting condition:', error);
        alert('Error deleting condition. Please try again.');
      }
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      currentStatus: '',
      reportedDate: '',
      endDate: '',
      status: 'All'
    });
  };

  const handleCloseViewOffcanvas = () => {
    console.log('Closing view offcanvas');
    setShowViewOffcanvas(false);
    setSelectedCondition(null);
  };

  const handleCloseAddOffcanvas = () => {
    console.log('Closing add offcanvas');
    if (!isSubmitting) {
      setShowAddOffcanvas(false);
    }
  };

  const handleCloseEditOffcanvas = () => {
    console.log('Closing edit offcanvas');
    if (!isSubmitting) {
      setShowEditOffcanvas(false);
      setEditingCondition(null);
    }
  };

  const handleCloseFilterOffcanvas = () => {
    setShowFilterOffcanvas(false);
  };

  const handleOpenFilterOffcanvas = () => {
    setShowAddOffcanvas(false);
    setShowViewOffcanvas(false);
    setShowFilterOffcanvas(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ongoing':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'observation':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'ongoing':
        return <FiActivity />;
      case 'resolved':
        return <FiCheck />;
      case 'observation':
        return <FiEye />;
      default:
        return <FiInfo />;
    }
  };

  const getRecordStatusColor = (status) => {
    return status === '1' ? 'success' : 'secondary';
  };

  const getRecordStatusText = (status) => {
    return status === '1' ? 'Active' : 'Inactive';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.currentStatus) {
      activeFilters.push({
        key: 'currentStatus',
        label: `Current Status: ${filters.currentStatus}`,
        value: filters.currentStatus,
        type: 'currentStatus'
      });
    }
    
    if (filters.reportedDate) {
      activeFilters.push({
        key: 'reportedDate',
        label: `From: ${formatDate(filters.reportedDate)}`,
        value: filters.reportedDate,
        type: 'reportedDate'
      });
    }
    
    if (filters.endDate) {
      activeFilters.push({
        key: 'endDate',
        label: `To: ${formatDate(filters.endDate)}`,
        value: filters.endDate,
        type: 'endDate'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: 'All' }));
        break;
      case 'currentStatus':
        setFilters(prev => ({ ...prev, currentStatus: '' }));
        break;
      case 'reportedDate':
        setFilters(prev => ({ ...prev, reportedDate: '' }));
        break;
      case 'endDate':
        setFilters(prev => ({ ...prev, endDate: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      currentStatus: '',
      reportedDate: '',
      endDate: '',
      status: 'All'
    });
    setSortBy('newest');
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/my-medical-conditions'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Conditions {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search conditions, diseases..."
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'status' ? 'active' : ''}`}
                          onClick={() => handleSortChange('status')}
                        >
                          By Status
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={handleOpenFilterOffcanvas}
                  >
                    <FiFilter />
                    Filter
                  </button>
            
                  {/* Add New Button */}
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={handleAddNew}
                  >
                    <FiPlus />
                    Add New
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                  <span className="text-muted fw-medium">Active filters:</span>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {getActiveFilters().map((filter) => (
                      <span
                        key={filter.key}
                        className="badge bg-primary d-flex align-items-center gap-1"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}
                      >
                        {filter.label}
                        <button
                          type="button"
                          className="btn-close btn-close-white"
                          style={{ 
                            fontSize: '0.625rem',
                            padding: '0.125rem',
                            marginLeft: '0.25rem'
                          }}
                          onClick={() => handleRemoveFilter(filter)}
                          title={`Remove ${filter.label} filter`}
                        ></button>
                      </span>
                    ))}
                    
                    {/* Clear All Button */}
                    {getActiveFilters().length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                        onClick={handleClearAllActiveFilters}
                        title="Clear all filters"
                      >
                        <FiX size={14} />
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Table */}
          <MaterialReactTable
            columns={columns}
            data={filteredConditions}
            
            // Basic configuration
            enableRowSelection
            enableColumnFilters
            enableGlobalFiltering
            enableSorting={false}
            enablePagination
            
            // Toolbars
            enableTopToolbar
            enableBottomToolbar
            
            // Optional features
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableColumnOrdering
            enableColumnResizing={false}
            enableGrouping={false}
            
            // States
            initialState={{
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: 'condition_name', desc: false }],
              density: 'comfortable',
              showGlobalFilter: false
            }}
            
            state={{
              isLoading,
              rowSelection,
              globalFilter
            }}
            
            // Callbacks
            onRowSelectionChange={setRowSelection}
            onGlobalFilterChange={handleGlobalFilterChange}
            
            // Rendering configuration
            muiTableContainerProps={{
              sx: { maxHeight: '600px' }
            }}
          />

          {/* Results Summary */}
          {filteredConditions.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">
                Showing {filteredConditions.length} of {conditions.length} conditions
                {Object.values(filters).some(f => f && f !== 'All') || globalFilter ? ' (filtered)' : ''}
              </small>
            </div>
          )}
        </div>
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseFilterOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1">
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Medical Conditions</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseFilterOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Current Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Current Status</label>
                  <select
                    className="form-select"
                    value={filters.currentStatus}
                    onChange={(e) => handleFilterChange('currentStatus', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="resolved">Resolved</option>
                    <option value="observation">Under Observation</option>
                  </select>
                </div>

                {/* Date Range Filters */}
                <div className="mb-3">
                  <label className="form-label">Reported From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.reportedDate}
                    onChange={(e) => handleFilterChange('reportedDate', e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Reported To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleApplyFilters}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Condition Offcanvas */}
      {showViewOffcanvas && selectedCondition && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Medical Condition Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row g-3">
                {/* Basic Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Basic Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Condition Name</label>
                          <p className="form-control-plaintext">
                            {selectedCondition.condition_name || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Disease</label>
                          <p className="form-control-plaintext">
                            {selectedCondition.disease_name || 'Not specified'}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Current Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getStatusColor(selectedCondition.current_status)} d-flex align-items-center gap-1`} style={{width: 'fit-content'}}>
                              {getStatusIcon(selectedCondition.current_status)}
                              {selectedCondition.current_status || 'Not specified'}
                            </span>
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Record Status</label>
                          <p className="form-control-plaintext">
                            <span className={`badge bg-${getRecordStatusColor(selectedCondition.status)}`}>
                              {getRecordStatusText(selectedCondition.status)}
                            </span>
                          </p>
                        </div>
                        {selectedCondition.disease_description && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Disease Description</label>
                            <p className="form-control-plaintext">
                              {selectedCondition.disease_description}
                            </p>
                          </div>
                        )}
                        {selectedCondition.comments && (
                          <div className="col-12">
                            <label className="form-label fw-bold">Comments</label>
                            <p className="form-control-plaintext">
                              {selectedCondition.comments}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Date Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiCalendar className="me-1" />
                            Reported On
                          </label>
                          <p className="form-control-plaintext">
                            {formatDate(selectedCondition.reported_on)}
                          </p>
                        </div>
                        {selectedCondition.resolved_on && (
                          <div className="col-md-6">
                            <label className="form-label fw-bold">
                              <FiCalendar className="me-1" />
                              Resolved On
                            </label>
                            <p className="form-control-plaintext">
                              {formatDate(selectedCondition.resolved_on)}
                            </p>
                          </div>
                        )}
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Created</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedCondition.created_date)}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Last Updated</label>
                          <p className="form-control-plaintext">
                            {formatDateTime(selectedCondition.updated_date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personnel Information */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Personnel Information</h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">
                            <FiUser className="me-1" />
                            Added By
                          </label>
                          <p className="form-control-plaintext">
                            {selectedCondition.added_by || 'Unknown'}
                          </p>
                          {selectedCondition.added_by_email && (
                            <small className="text-muted">
                              {selectedCondition.added_by_email}
                            </small>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Patient</label>
                          <p className="form-control-plaintext">
                            {selectedCondition.patient_name || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="col-12">
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-outline-primary flex-fill"
                      onClick={() => {
                        handleCloseViewOffcanvas();
                        setTimeout(() => handleEditCondition(selectedCondition.condition_id), 300);
                      }}
                    >
                      <FiEdit3 className="me-2" />
                      Edit Condition
                    </button>
                    <button 
                      className="btn btn-outline-danger flex-fill"
                      onClick={() => handleDeleteCondition(selectedCondition.condition_id)}
                    >
                      <FiTrash2 className="me-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add New Condition Offcanvas */}
      {showAddOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseAddOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Add New Medical Condition</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseAddOffcanvas}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="offcanvas-body">
              <AddConditionForm
                onSubmit={handleSubmitNewCondition}
                onCancel={handleCloseAddOffcanvas}
                isSubmitting={isSubmitting}
                patientData={patientData}
              />
            </div>
          </div>
        </>
      )}

      {/* Edit Condition Offcanvas */}
      {showEditOffcanvas && editingCondition && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseEditOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{width: '600px'}}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Edit Medical Condition</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseEditOffcanvas}
                disabled={isSubmitting}
              ></button>
            </div>
            <div className="offcanvas-body">
              <EditConditionForm
                condition={editingCondition}
                onSubmit={handleUpdateCondition}
                onCancel={handleCloseEditOffcanvas}
                isSubmitting={isSubmitting}
                patientData={patientData}
              />
            </div>
          </div>
        </>
      )}

      {/* Patient Clinical Data Component */}
      <PatientMedicalClinicalData 
        patientData={patientData}
        alertInfo={alertInfo}
      />
    </main>
  );
};

// AddConditionForm Component
const AddConditionForm = ({ onSubmit, onCancel, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    conditionname: '',
    disease: '',
    currentstatus: 'ongoing',
    reportedon: new Date().toISOString().split('T')[0],
    resolvedon: '',
    comments: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.conditionname.trim() && !formData.disease.trim()) {
  newErrors.conditionname = 'Either condition name or disease must be specified';
}

    if (!formData.currentstatus) {
      newErrors.currentstatus = 'Current status is required';
    }

    if (!formData.reportedon) {
      newErrors.reportedon = 'Reported date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
      // Reset form on success
      setFormData({
        conditionname: '',
        disease: '',
        currentstatus: 'ongoing',
        reportedon: new Date().toISOString().split('T')[0],
        resolvedon: '',
        comments: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Patient Info */}
        <div className="col-12">
          <div className="alert alert-info">
            <FiUser className="me-2" />
            Adding condition for: <strong>{patientData?.patientName || 'Unknown Patient'}</strong>
          </div>
        </div>

        {/* Condition Name */}
        <div className="col-12">
          <label className="form-label">Condition Name</label>
          <input
            type="text"
            className={`form-control ${errors.conditionname ? 'is-invalid' : ''}`}
            value={formData.conditionname}
            onChange={(e) => handleInputChange('conditionname', e.target.value)}
            placeholder="e.g., Diabetes Type 2, Hypertension"
            disabled={isSubmitting}
          />
          {errors.conditionname && <div className="invalid-feedback">{errors.conditionname}</div>}
        </div>

        {/* Disease (if you have a disease reference collection) */}
        <div className="col-12">
          <label className="form-label">Disease (Optional)</label>
          <input
            type="text"
            className="form-control"
            value={formData.disease}
            onChange={(e) => handleInputChange('disease', e.target.value)}
            placeholder="Disease ID or reference"
            disabled={isSubmitting}
          />
          <small className="text-muted">Leave blank if not referencing a specific disease record</small>
        </div>

        {/* Current Status */}
        <div className="col-md-6">
          <label className="form-label required">Current Status *</label>
          <select
            className={`form-select ${errors.currentstatus ? 'is-invalid' : ''}`}
            value={formData.currentstatus}
            onChange={(e) => handleInputChange('currentstatus', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select status</option>
            <option value="ongoing">Ongoing</option>
            <option value="resolved">Resolved</option>
            <option value="observation">Under Observation</option>
          </select>
          {errors.currentstatus && <div className="invalid-feedback">{errors.currentstatus}</div>}
        </div>

        {/* Reported Date */}
        <div className="col-md-6">
          <label className="form-label required">Reported Date *</label>
          <input
            type="date"
            className={`form-control ${errors.reportedon ? 'is-invalid' : ''}`}
            value={formData.reportedon}
            onChange={(e) => handleInputChange('reportedon', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.reportedon && <div className="invalid-feedback">{errors.reportedon}</div>}
        </div>

        {/* Resolved Date */}
        {formData.currentstatus === 'resolved' && (
          <div className="col-md-6">
            <label className="form-label">Resolved Date</label>
            <input
              type="date"
              className="form-control"
              value={formData.resolvedon}
              onChange={(e) => handleInputChange('resolvedon', e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        )}

        {/* Comments */}
        <div className="col-12">
          <label className="form-label">Additional Comments</label>
          <textarea
            className="form-control"
            rows="4"
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Any additional notes, symptoms, or observations"
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FiCheck className="me-2" />
                  Create Condition
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

// EditConditionForm Component
const EditConditionForm = ({ condition, onSubmit, onCancel, isSubmitting, patientData }) => {
  const [formData, setFormData] = useState({
    conditionname: condition.condition_name || '',
    disease: condition.disease_name || '',
    currentstatus: condition.current_status || 'ongoing',
    reportedon: condition.reported_on ? new Date(condition.reported_on).toISOString().split('T')[0] : '',
    resolvedon: condition.resolved_on ? new Date(condition.resolved_on).toISOString().split('T')[0] : '',
    comments: condition.comments || ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.conditionname.trim() && !formData.disease) {
      newErrors.conditionname = 'Either condition name or disease must be specified';
    }

    if (!formData.currentstatus) {
      newErrors.currentstatus = 'Current status is required';
    }

    if (!formData.reportedon) {
      newErrors.reportedon = 'Reported date is required';
    }

    // If status is resolved, resolved date should be provided
    if (formData.currentstatus === 'resolved' && !formData.resolvedon) {
      newErrors.resolvedon = 'Resolved date is required when status is resolved';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error updating condition:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Patient Info */}
        <div className="col-12">
          <div className="alert alert-info">
            <FiUser className="me-2" />
            Editing condition for: <strong>{patientData?.patientName || 'Unknown Patient'}</strong>
          </div>
        </div>

        {/* Condition Name */}
        <div className="col-12">
          <label className="form-label">Condition Name</label>
          <input
            type="text"
            className={`form-control ${errors.conditionname ? 'is-invalid' : ''}`}
            value={formData.conditionname}
            onChange={(e) => handleInputChange('conditionname', e.target.value)}
            placeholder="e.g., Diabetes Type 2, Hypertension"
            disabled={isSubmitting}
          />
          {errors.conditionname && <div className="invalid-feedback">{errors.conditionname}</div>}
        </div>

        {/* Disease */}
        <div className="col-12">
          <label className="form-label">Disease (Optional)</label>
          <input
            type="text"
            className="form-control"
            value={formData.disease}
            onChange={(e) => handleInputChange('disease', e.target.value)}
            placeholder="Disease ID or reference"
            disabled={isSubmitting}
          />
          <small className="text-muted">Leave blank if not referencing a specific disease record</small>
        </div>

        {/* Current Status */}
        <div className="col-md-6">
          <label className="form-label required">Current Status *</label>
          <select
            className={`form-select ${errors.currentstatus ? 'is-invalid' : ''}`}
            value={formData.currentstatus}
            onChange={(e) => handleInputChange('currentstatus', e.target.value)}
            disabled={isSubmitting}
          >
            <option value="">Select status</option>
            <option value="ongoing">Ongoing</option>
            <option value="resolved">Resolved</option>
            <option value="observation">Under Observation</option>
          </select>
          {errors.currentstatus && <div className="invalid-feedback">{errors.currentstatus}</div>}
        </div>

        {/* Reported Date */}
        <div className="col-md-6">
          <label className="form-label required">Reported Date *</label>
          <input
            type="date"
            className={`form-control ${errors.reportedon ? 'is-invalid' : ''}`}
            value={formData.reportedon}
            onChange={(e) => handleInputChange('reportedon', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.reportedon && <div className="invalid-feedback">{errors.reportedon}</div>}
        </div>

        {/* Resolved Date */}
        <div className="col-md-6">
          <label className="form-label">
            Resolved Date
            {formData.currentstatus === 'resolved' && <span className="text-danger"> *</span>}
          </label>
          <input
            type="date"
            className={`form-control ${errors.resolvedon ? 'is-invalid' : ''}`}
            value={formData.resolvedon}
            onChange={(e) => handleInputChange('resolvedon', e.target.value)}
            disabled={isSubmitting}
          />
          {errors.resolvedon && <div className="invalid-feedback">{errors.resolvedon}</div>}
          {formData.currentstatus === 'resolved' && (
            <small className="text-muted">Required when status is resolved</small>
          )}
        </div>

        {/* Comments */}
        <div className="col-12">
          <label className="form-label">Additional Comments</label>
          <textarea
            className="form-control"
            rows="4"
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Any additional notes, symptoms, or observations"
            disabled={isSubmitting}
          />
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Updating...
                </>
              ) : (
                <>
                  <FiCheck className="me-2" />
                  Update Condition
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MedicalConditions;